import React, { useCallback, useEffect } from 'react';
import { InputProps } from '@material-ui/core/Input';
import { useField, useFormikContext } from 'formik';
import debounce from 'debounce';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, FormHelperText } from '@material-ui/core';
import { FormInput } from '..';
import {
  clearValidationPromotionCodeWIthProduct,
  validatePromotionCodeWithProductRequest,
} from '../../store/modules/promotion-codes/actions';
import { selectVerifiedCode } from '../../store/modules/promotion-codes/selectors';
import StatusIcon from '../password-error/StatusIcon';
import useStyles from './styles';
import { getPromoCodeFromStore } from '../../utility/promotionCode';
import { selectCurrentUser } from '../../store/modules/users/selectors';
import InlineDots from './InlineDots';

type Props = Omit<InputProps, 'classes'> & {
  label?: string;
  name: string;
  autoCheck?: boolean;
  className?: string;
  disableSavedPromo?: boolean;
};

const FormPromotionCodeInput = ({
  name,
  label,
  autoCheck = false,
  className,
  disableSavedPromo = false,
}: Props) => {
  const { setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const [{ value }] = useField({ name });
  const { data: promoCode, loading, error } = useSelector(selectVerifiedCode);
  const classes = useStyles();
  const { data: user } = useSelector(selectCurrentUser);

  useEffect(() => {
    dispatch(clearValidationPromotionCodeWIthProduct());

    if (!disableSavedPromo) {
      const savedPromoCode = getPromoCodeFromStore(Boolean(user));
      if (savedPromoCode) {
        setFieldValue(name as never, savedPromoCode);
      }
    }
  }, [dispatch, name, setFieldValue, user, disableSavedPromo]);

  const checkPromotionCode = useCallback((code: string) => {
    if (autoCheck) {
      if (code && code.length > 7) {
        dispatch(validatePromotionCodeWithProductRequest(code));
      } else {
        dispatch(clearValidationPromotionCodeWIthProduct());
      }
    }
  }, [dispatch, autoCheck]);

  const checkPromotionCodeHandler = useCallback(debounce((search: string) => { checkPromotionCode(search); }, 1500),
    [checkPromotionCode]);

  useEffect(() => {
    checkPromotionCodeHandler(value);
  }, [value, checkPromotionCodeHandler]);

  return (
    <FormControl
      fullWidth
      className={className}
    >
      <FormInput
        className={classes.uppercaseField}
        label={label}
        name={name}
        hideErrors
      />
      {loading && <FormHelperText>Verifying<InlineDots/></FormHelperText>}
      {error && (
        <FormHelperText className={`${classes.helperText} ${classes.helperTextError}`}>
          <StatusIcon state={false} className={classes.icon} />
          Sorry, this Promo Code is not valid
        </FormHelperText>
      )}
      {promoCode && !loading && (
        <FormHelperText className={`${classes.helperText} ${classes.helperTextSuccess}`}>
          <StatusIcon state className={classes.icon} />
          Promo Code accepted for {promoCode.summary}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormPromotionCodeInput;
